/**
 * The two hierarchies of the ontology (spec 2026-10-09-04):
 *
 * - **inheritance**: a type and the subtypes that inherit from it
 *   (Lot → Habitation, Parking, Cave). Read from `inherits_from_asset_type_uid`.
 * - **possible children**: the types an *instance* of a type may hold
 *   (Habitation → Room, Equipment). Read from the children's
 *   `possible_parent_asset_type_uids`, where a rule written against a type also
 *   covers its subtypes: equipment that accepts `unit` accepts a `unit_housing`.
 *
 * Both are built here, from the flat type list, so the asset-types page shows
 * them with one tree component and the create flows agree with the API's
 * `assetTypeAcceptsParent`.
 */

export interface TypeNodeInput {
  uid: string
  code?: string
  inherits_from_asset_type_uid?: string | null
  possible_parent_asset_type_uids?: string[] | null
}

export interface TypeTreeNode<T extends TypeNodeInput> {
  type: T
  children: TypeTreeNode<T>[]
}

const MAX_DEPTH = 16

/** `unit` and every `unit_*` subtype: the `unit_` prefix is the ontology's contract for a lot. */
export function isLotTypeCode(code: string | undefined | null): boolean {
  return code === 'unit' || (code?.startsWith('unit_') ?? false)
}

/** Folds a lot subtype onto `unit`; any other code is returned unchanged. */
export function lotBaseTypeCode(code: string | undefined | null): string {
  if (!code) return ''
  return isLotTypeCode(code) ? 'unit' : code
}

/** The type's uid followed by the uids of every type it inherits from. */
export function ancestorUids<T extends TypeNodeInput>(uid: string, byUid: Map<string, T>): string[] {
  const out: string[] = []
  let current: string | undefined | null = uid
  for (let i = 0; i < MAX_DEPTH && current && !out.includes(current); i++) {
    out.push(current)
    current = byUid.get(current)?.inherits_from_asset_type_uid
  }
  return out
}

/**
 * Whether an instance of `parentTypeUid` may hold an instance of `child`,
 * judged on the child's own list: the parent type, or a type it inherits from,
 * has to appear in it.
 */
export function acceptsParentType<T extends TypeNodeInput>(child: T, parentTypeUid: string, byUid: Map<string, T>): boolean {
  const allowed = child.possible_parent_asset_type_uids ?? []
  if (allowed.length === 0) return false
  return ancestorUids(parentTypeUid, byUid).some((uid) => allowed.includes(uid))
}

function sortNodes<T extends TypeNodeInput>(nodes: TypeTreeNode<T>[], label: (t: T) => string): TypeTreeNode<T>[] {
  nodes.sort((a, b) => label(a.type).localeCompare(label(b.type)))
  return nodes
}

/** Every type with no (known) parent type at the root, its subtypes below. */
export function buildInheritanceTree<T extends TypeNodeInput>(types: T[], label: (t: T) => string): TypeTreeNode<T>[] {
  const byUid = new Map(types.map((t) => [t.uid, t]))
  const nodes = new Map(types.map((t) => [t.uid, { type: t, children: [] } as TypeTreeNode<T>]))
  const roots: TypeTreeNode<T>[] = []
  for (const t of types) {
    const parentUid = t.inherits_from_asset_type_uid
    const parent = parentUid && parentUid !== t.uid && byUid.has(parentUid) ? nodes.get(parentUid) : undefined
    if (parent) parent.children.push(nodes.get(t.uid)!)
    else roots.push(nodes.get(t.uid)!)
  }
  const sortDeep = (list: TypeTreeNode<T>[]) => {
    sortNodes(list, label)
    list.forEach((n) => sortDeep(n.children))
  }
  sortDeep(roots)
  return roots
}

/** The types an instance of `parentTypeUid` may directly hold. */
export function possibleChildTypes<T extends TypeNodeInput>(parentTypeUid: string, types: T[]): T[] {
  const byUid = new Map(types.map((t) => [t.uid, t]))
  return types.filter((t) => t.uid !== parentTypeUid && acceptsParentType(t, parentTypeUid, byUid))
}

/**
 * The possible-children tree under `rootUid`: each node lists the types its
 * instances may hold, `depth` levels deep. A type already on the path is not
 * expanded again, so the ontology's legitimate cycles (a room under a room's
 * parent) never recurse forever.
 */
export function buildPossibleChildrenTree<T extends TypeNodeInput>(
  rootUid: string,
  types: T[],
  label: (t: T) => string,
  depth = 2,
): TypeTreeNode<T> | undefined {
  const root = types.find((t) => t.uid === rootUid)
  if (!root) return undefined
  const expand = (type: T, level: number, path: Set<string>): TypeTreeNode<T> => {
    if (level >= depth) return { type, children: [] }
    const next = new Set(path).add(type.uid)
    const children = possibleChildTypes(type.uid, types)
      .filter((c) => !next.has(c.uid))
      .map((c) => expand(c, level + 1, next))
    return { type, children: sortNodes(children, label) }
  }
  return expand(root, 0, new Set())
}

/** The subtree of the inheritance tree rooted at `rootUid`, if present. */
export function findTreeNode<T extends TypeNodeInput>(roots: TypeTreeNode<T>[], rootUid: string): TypeTreeNode<T> | undefined {
  for (const n of roots) {
    if (n.type.uid === rootUid) return n
    const found = findTreeNode(n.children, rootUid)
    if (found) return found
  }
  return undefined
}
